/** Fixed lookback presets for PDC historical comparison (days ending on workout day). */
export const PDC_COMPARE_PRESET_DAYS = [7, 14, 28, 90, 180, 365] as const;

export type PdcComparePresetDays = (typeof PDC_COMPARE_PRESET_DAYS)[number];

export type PdcComparePreset = PdcComparePresetDays | 'all' | 'custom';

const ALL_RANGE_START = '2000-01-01';

function parseIsoDay(iso: string): Date {
  return new Date(iso.length > 10 ? iso : `${iso}T12:00:00`);
}

function toIsoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Compare window ending on `rangeEnd` (usually workout day). */
export function compareRangeForPreset(
  rangeEnd: string,
  preset: PdcComparePreset,
  customStart?: string,
  customEnd?: string,
): { start: string; end: string } {
  const end = (rangeEnd || toIsoDay(new Date())).slice(0, 10);

  if (preset === 'custom') {
    const start = (customStart ?? '').slice(0, 10);
    const customEndIso = (customEnd ?? end).slice(0, 10);
    return { start: start || end, end: customEndIso };
  }

  if (preset === 'all') {
    return { start: ALL_RANGE_START, end };
  }

  const d = parseIsoDay(end);
  d.setDate(d.getDate() - preset);
  return { start: toIsoDay(d), end };
}

/** Highlight chip when stored dates match a fixed preset. */
export function detectComparePreset(
  start: string,
  end: string,
  rangeEnd: string,
): PdcComparePreset {
  const endDay = end.slice(0, 10);
  const anchor = rangeEnd.slice(0, 10);
  if (endDay !== anchor) return 'custom';

  for (const days of PDC_COMPARE_PRESET_DAYS) {
    const r = compareRangeForPreset(anchor, days);
    if (r.start === start.slice(0, 10)) return days;
  }

  const all = compareRangeForPreset(anchor, 'all');
  if (all.start === start.slice(0, 10)) return 'all';

  return 'custom';
}
